import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';
import { saveUserProgress } from '../data/database';

function SaveButton({ lessonType, progressData, onSaveSuccess, className = "" }) {
  const { user } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null

  const handleSave = async () => {
    if (!user) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const progressToSave = {
        [lessonType]: progressData,
        email: user.primaryEmailAddress?.emailAddress
      };
      
      await saveUserProgress(user.id, progressToSave);
      
      setSaveStatus('success');
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      // Clear success message after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error saving progress:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return null; // Don't show save button if user is not logged in
  }

  return (
    <div className={`relative ${className}`}>
      <motion.button
        onClick={handleSave}
        disabled={isSaving}
        className={`
          px-6 py-3 rounded-full font-semibold text-white
          transition-all duration-300 transform
          ${isSaving 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105'
          }
          shadow-lg hover:shadow-xl
          disabled:opacity-50
        `}
        whileHover={!isSaving ? { scale: 1.05 } : {}}
        whileTap={!isSaving ? { scale: 0.95 } : {}}
      >
        {isSaving ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>حفظ...</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>حفظ التقدم</span>
          </div>
        )}
      </motion.button>

      {/* Status Messages */}
      <AnimatePresence>
        {saveStatus && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            className={`
              absolute top-full mt-2 left-1/2 transform -translate-x-1/2
              px-4 py-2 rounded-lg text-sm font-medium
              ${saveStatus === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
              }
              shadow-lg z-10
            `}
          >
            {saveStatus === 'success' ? 'تم الحفظ بنجاح!' : 'خطأ في الحفظ'}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SaveButton;
